import { Form, Input, Button, message, Switch } from 'antd'
import editAdmin, { editAdminBody } from 'api/admin/editAdmin'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
// eslint-disable-next-line import/no-extraneous-dependencies
import { ValidateErrorEntity } from 'rc-field-form/lib/interface'
import React, { useState } from 'react'
import { getAdminsAction } from 'slices/admins'
import { AdminUser } from 'types'
import handleFormError from 'utils/handleFormError'

interface PropTypes {
	closeDrawer: Function
	singleAdmin: AdminUser
}

/**
 * Form Component for Editing Single Admin in admin table
 *
 * @param closeDrawer - function which get triggered when edit admin drawer get close
 * @returns updated data of the admin
 */
const EditAdminForm: React.FC<PropTypes> = ({ closeDrawer, singleAdmin }) => {
	const [loading, setLoading] = useState<boolean>(false)
	const { currentPage } = useAppSelector(state => state.admin)
	const dispatch = useAppDispatch()

	// eslint-disable-next-line consistent-return
	const onFinish = async (values: editAdminBody) => {
		const body = {
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			mobile: values.mobile,
			active: values.active,
			password: values.password === '' ? null : values.password,
		}
		const id = singleAdmin._id
		try {
			setLoading(true)
			await editAdmin({ body, id })
			setLoading(false)
			const options = {
				page: currentPage,
				size: 10,
				query: '',
			}
			dispatch(getAdminsAction(options))
			closeDrawer()
			message.success('Admin Updated Successfully')
		} catch (error: any) {
			setLoading(false)
		}
	}

	const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
		handleFormError(errorInfo)
	}

	return (
		<Form
			name='editAdmin'
			onFinish={onFinish}
			onFinishFailed={onFinishFailed}
			layout='vertical'
			initialValues={{
				firstName: singleAdmin.firstName || '',
				lastName: singleAdmin.lastName || '',
				email: singleAdmin.email || '',
				mobile: singleAdmin.mobile || '',
				password: null,
				active: singleAdmin.active || '',
			}}
		>
			<Form.Item
				label='First Name'
				name='firstName'
				rules={[
					{
						required: true,
						message: 'First name is required',
					},
				]}
			>
				<Input />
			</Form.Item>

			<Form.Item
				label='Last Name'
				name='lastName'
				rules={[
					{
						required: true,
						message: 'Last name is required',
					},
				]}
			>
				<Input />
			</Form.Item>

			<Form.Item
				label='Email'
				name='email'
				rules={[
					{
						required: true,
						message: 'Email is required',
					},
					{
						pattern:
							/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
						message: 'Please enter a valid Email',
					},
				]}
			>
				<Input />
			</Form.Item>
			<Form.Item
				label='Mobile Number'
				name='mobile'
				rules={[
					{ required: true, message: 'Mobile no. is Required' },
					{
						pattern: /^(\+?91|0)?[6789]\d{9}$/,
						message: 'Please enter a valid mobile no.',
					},
				]}
			>
				<Input />
			</Form.Item>
			<Form.Item
				label='Password'
				name='password'
				rules={[
					{
						pattern: /^[a-zA-Z0-9!@#$%^&*]{8,64}$/,
						message: 'Please provide strong password',
					},
				]}
			>
				<Input.Password />
			</Form.Item>
			<Form.Item label='Active' name='active' valuePropName='checked'>
				<Switch checkedChildren='Active' unCheckedChildren='Inactive' />
			</Form.Item>

			<Form.Item>
				<Button type='primary' htmlType='submit' block loading={loading}>
					Edit Admin
				</Button>
			</Form.Item>
		</Form>
	)
}

export default EditAdminForm
